import { useState, useEffect } from "react";
import { Link, useLocation } from "@/lib/router-compat";
import ResponsiveImage from "@/components/ResponsiveImage";
import { IMG } from "@/lib/optimized-images";
import { Menu, X } from "lucide-react";

interface NavItem {
  label: string;
  href: string;
}

const NAV_ITEMS: NavItem[] = [
  { label: "Home", href: "/" },
  { label: "About Us", href: "/about-us" },
  { label: "Services", href: "/services" },
  { label: "Projects", href: "/projects" },
  { label: "Insights", href: "/insights" },
  { label: "Careers", href: "/careers" },
  { label: "Contact Us", href: "/contact" },
];

export default function Navbar({
  forceSolid = false,
}: {
  forceSolid?: boolean;
}) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const isSolid = forceSolid || scrolled;

  const renderNavItem = (item: NavItem) => {
    const isExternalOrAnchor = item.href.includes("#");
    const isActive =
      item.href === "/"
        ? location.pathname === "/"
        : location.pathname.startsWith(item.href);

    const baseClasses =
      "relative block px-3 xl:px-4 py-2 text-base font-heading font-semibold transition-colors whitespace-nowrap uppercase tracking-wider";
    const activeClasses = isActive
      ? "text-[#ffcc29] after:content-[''] after:absolute after:bottom-0 after:left-3 after:right-3 after:h-0.5 after:bg-[#ffcc29]"
      : "text-white/90 hover:text-[#ffcc29]";

    return (
      <div key={item.label} className="h-full flex items-center">
        {isExternalOrAnchor ? (
          <a href={item.href} className={`${baseClasses} ${activeClasses}`}>
            {item.label}
          </a>
        ) : (
          <Link to={item.href} className={`${baseClasses} ${activeClasses}`}>
            {item.label}
          </Link>
        )}
      </div>
    );
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isSolid ? "bg-[#002b5c] shadow-lg" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 md:px-12 flex items-center justify-between h-20 relative">
        {/* Mobile Header (Left Logo, Right Hamburger) */}
        <div className="flex lg:hidden items-center justify-between w-full">
          <Link to="/" className="flex items-center shrink-0">
            <ResponsiveImage
              rendition={IMG.logo}
              alt="Abacus Design Solutions"
              width={95}
              height={40}
              className="h-10 w-auto"
            />
          </Link>
          <button
            className="text-white p-2"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle navigation menu"
          >
            {mobileOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* Desktop Layout: Logo on Left, Navigation in Middle, CTA on Right */}
        <div className="hidden lg:flex items-center justify-between w-full h-full">
          {/* Left Logo */}
          <Link
            to="/"
            className="flex items-center shrink-0 pr-6 xl:pr-10 group"
          >
            <ResponsiveImage
              rendition={IMG.logo}
              alt="Abacus Design Solutions"
              width={140}
              height={58}
              className="h-[3.355rem] xl:h-[3.63rem] w-auto transition-transform group-hover:scale-105"
            />
          </Link>

          {/* Navigation Links */}
          <nav className="flex items-center gap-1 xl:gap-3 h-full">
            {NAV_ITEMS.map(renderNavItem)}
          </nav>

          {/* Right CTA */}
          <div className="flex items-center shrink-0">
            <Link
              to="/contact"
              className="bg-[#ffcc29] text-[#002b5c] px-5 py-2.5 rounded-xs font-heading font-bold text-sm hover:bg-white transition-all uppercase tracking-wider whitespace-nowrap"
            >
              Get a Quote
            </Link>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden absolute top-full left-0 right-0 bg-[#002b5c] border-t border-white/10 max-h-[calc(100dvh-5rem)] overflow-y-auto pb-12 overscroll-contain">
          {NAV_ITEMS.map((item) => {
            const isActive =
              item.href === "/"
                ? location.pathname === "/"
                : location.pathname.startsWith(item.href);
            return (
              <div key={item.label} className="border-b border-white/5">
                {item.href.includes("#") ? (
                  <a
                    href={item.href}
                    onClick={() => setMobileOpen(false)}
                    className={`block px-8 py-5 font-body font-bold text-sm transition-colors uppercase tracking-wider ${
                      isActive
                        ? "text-[#ffcc29] bg-white/5"
                        : "text-white hover:text-[#ffcc29]"
                    }`}
                  >
                    {item.label}
                  </a>
                ) : (
                  <Link
                    to={item.href}
                    onClick={() => setMobileOpen(false)}
                    className={`block px-8 py-5 font-body font-bold text-sm transition-colors uppercase tracking-wider ${
                      isActive
                        ? "text-[#ffcc29] bg-white/5"
                        : "text-white hover:text-[#ffcc29]"
                    }`}
                  >
                    {item.label}
                  </Link>
                )}
              </div>
            );
          })}
          <div className="px-8 mt-8">
            <Link
              to="/contact"
              onClick={() => setMobileOpen(false)}
              className="block text-center py-4 text-[#002b5c] bg-[#ffcc29] font-heading font-bold text-sm tracking-widest uppercase rounded-xs"
            >
              Get a Quote
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
